import { useLoc } from '@app/shared/lib/hooks';
import React from 'react';
import style from './MarkdownHelp.module.scss';
import { Button, Popover, Tooltip } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { IoLogoMarkdown } from 'react-icons/io';

type Props = {
  minimal?: boolean;
}

const MarkdownHelp: React.FC<Props> = ({ minimal = false }) => {

  const loc = useLoc();

  return <div className={style.md_help}>
    <div>
      <div>
        <p>{loc.word('Markdown.help', { default: 'Accel uses Markdown for formatting. Here are the basics.' })}</p>
        <hr />

        {!minimal && <>
          <p>{loc.word('Markdown.headersLabel', { default: 'Headers' })}</p>
          <code>{loc.word('Markdown.header1Label', { default: '# Header 1' })}</code>
          <br />
          <code>{loc.word('Markdown.header2Label', { default: '## Header 2' })}</code>
          <br />
          <code>{loc.word('Markdown.header3Label', { default: '### Header 3' })}</code>
          <br />
          <code>{loc.word('Markdown.header4Label', { default: '#### Header 4' })}</code>
          <br />
          <code>{loc.word('Markdown.header5Label', { default: '##### Header 5' })}</code>
          <br />
          <code>{loc.word('Markdown.header6Label', { default: '###### Header 6' })}</code>

          <hr />

          <p>{loc.word('Markdown.paragraphsLabel', { default: 'Paragraphs' })}</p>
          <code dangerouslySetInnerHTML={{
            __html: loc.word('Markdown.paragraphsCode', {
              default: `To create paragraphs, use a blank line to separate one or more lines of text.
                                <br />
                                <br />
                                I think I'll use it to format all of my documents from now on.` })
          }}>
          </code>

          <hr />
        </>}

        <p>{loc.word('Markdown.boldItalicLabel', { default: 'Bold and Italic' })}</p>
        <code>{loc.word('Markdown.boldCode', { default: "It's a **bold text**." })}</code>
        <br />
        <code>{loc.word('Markdown.italicCode', { default: "It's an *italic text*" })}</code>
        <br />
        <code>{loc.word('Markdown.boldItalicCode', { default: "This text is ***bold and italic at the same time***." })}</code>

        <hr />
        <p>{loc.word('Markdown.strikethroughLabel', { default: 'Strikethrough' })}</p>
        <code>{loc.word('Markdown.strikethroughCode', { default: "It's a ~strikethrough text~ and ~~this too~~" })}</code>

        <hr />

        {!minimal && <>
          <p>{loc.word('Markdown.listsLabel', { default: 'Lists' })}</p>
          <code dangerouslySetInnerHTML={{
            __html: loc.word('Markdown.listsCode', {
              default: `- First item
                                    <br />
                                    - Second item
                                    <br />
                                    - Third item
                                    <br />
                                    - Fourth item
                                    ` })
          }}></code>

          <hr />
        </>}

        <p>{loc.word('Markdown.linksLabel', { default: 'Links' })}</p>
        <code>{loc.word('Markdown.linksCode', { default: "Click [here](Link here) to navigate" })}</code>

        {!minimal && <>
          <hr />

          <p>{loc.word('Markdown.imageLabel', { default: 'Image' })}</p>
          <code>{loc.word('Markdown.imageCode', { default: '![You will see image here](Image link)' })}</code>
        </>}
      </div>
    </div>
  </div>
}

export default MarkdownHelp;

type MarkdownHelpPopoverProps = {
  children?: React.ReactNode;
  popoverProps?: any;
  iconOnly?: boolean;
  withModalContainer?: boolean;
  minimal?: boolean;
}

const MarkdownHelpPopover: React.FC<MarkdownHelpPopoverProps> = ({
  children,
  popoverProps,
  iconOnly = false,
  withModalContainer = false,
  minimal = false
}) => {
  const loc = useLoc();

  const getPopupContainer = (triggerNode: HTMLElement): HTMLElement => {
    if (!withModalContainer) return document.body;

    let element: HTMLElement | null = triggerNode;
    while (element) {
      if (element.classList.contains("ant-modal-container") || element.classList.contains("ant-modal")) {
        return element;
      }
      element = element.parentElement;
    }
    return document.body;
  };

  const popoverConfig = {
    trigger: 'click' as const,
    title: loc.word("Form.settings.formattingHelp", { default: "Help with formatting" }),
    content: <MarkdownHelp minimal={minimal} />,
    ...(withModalContainer && {
      getPopupContainer,
      autoAdjustOverflow: false,
      placement: 'top' as const
    }),
    ...popoverProps
  };

  const defaultButton = iconOnly
    ? <Tooltip title={loc.word("Form.settings.mdSupport.short", { default: "Field supports Markdown" })}>
      <Button icon={<IoLogoMarkdown />} type="text" size="large" />
    </Tooltip>
    : <Button icon={<InfoCircleOutlined />} type="link" className="p-0 fs-13" size="small">
      {loc.word("Form.settings.mdSupport.short", { default: "Field supports Markdown" })}
    </Button>;

  return <Popover {...popoverConfig}>
    {children ?? defaultButton}
  </Popover>;
};


export { MarkdownHelpPopover }
